// Transfer section of the hotel Details window (QA doc docs/features/iway-transfers/qa.md R-HT16 .. R-HT21). Pure: no I/O, no clock.
import { CheckoutTouristOut, CheckoutTransferOut, CheckoutTransferPrice } from '@appTypes/api.types';
import { percentOf, roundCents, sumCents } from '@shared/services/shown-money.service';

export type TransferRequirement = 'optional' | 'mandatory' | 'included';

/** What the agent chose in the Details window; the checkout opens with it (R-HT19). */
export interface ICartHotelTransfer {
  terminalCode: string;
  arrivalCarId: string | null;
  departureCarId: string | null;
  /** The cars as the agent saw them - the checkout falls back to source + name when a live car's id changed. */
  arrivalCar: Pick<CheckoutTransferPrice, 'name' | 'source'> | null;
  departureCar: Pick<CheckoutTransferPrice, 'name' | 'source'> | null;
  requirement: TransferRequirement;
  /**
   * The price of each chosen leg for the party (per vehicle, or per person x tourists) in the package currency, rounded to the cent - one
   * RFTransferService line each at registration (R-HT22). Absent on a cart item kept from before 09.10.2026.
   */
  legs?: number[];
  /** The transfer's price for the party = the sum of `legs`. */
  price: number;
}

/** The leg of a transfer block. */
export const legOf = (block: CheckoutTransferOut | undefined | null): 'arrival' | 'departure' | null => {
  if (block?.type === 'Arrival') return 'arrival';
  if (block?.type === 'Departure') return 'departure';
  return null;
};

/** The real cars of a block: the core's "WITHOUT" placeholder is not a car (R-HT11). */
export const carsOf = (block: CheckoutTransferOut | undefined | null): CheckoutTransferPrice[] =>
  (block?.transferPrices ?? []).filter((p) => !!p.id && !/WITHOUT/i.test(p.name ?? ''));

/**
 * One car's price for the party, as the server answers it - counted once. A per-vehicle car's `adultPrice` is the car; a per-person price's
 * `adultPrice` is ALREADY the party's total (packages-api CreateOutputTransfer: TotalPrice + SurR, the same figure the reservation line stores -
 * docs processes/transfer-prices.md R-TP-W1, 16.10.2026). Multiplying it by the tourists again showed 2 x the line for two adults.
 */
export const carPrice = (car: CheckoutTransferPrice | null | undefined, _adults: number, _children: number): number =>
  car?.adultPrice ?? 0;

/** One leg's price as the reservation line stores it: the party's price rounded to the cent (R-HT22). */
export const legPrice = (car: CheckoutTransferPrice | null | undefined, adults: number, children: number): number =>
  roundCents(carPrice(car, adults, children));

/** The Details window's money as the agent reads it (R-HT22 / R-HT23), in the shown currency. */
export interface IShownHotelTotals {
  currency: string;
  hotel: number;
  /** each chosen leg, shown */
  legs: number[];
  transfer: number;
  total: number;
  /** the agent's commission percent of the hotel package - registration applies the same percent to every transfer line */
  percent: number;
  commission: number;
  payable: number;
}

/**
 * R-HT22 / R-HT23: every part converted and rounded to the cent on its own, the totals summed from the shown parts - so "Hotel + transfer =
 * total" and Total / Amount to pay / Commission add up on the screen. The transfer is commissioned like its reservation line: packages-api
 * CheckoutService.CreateRFTransfer stores each leg as PRSum = sell - Perc(sell, commission %) and SurchargeAmount = Perc(sell, commission %),
 * with the hotel package's commission percent; the reservation total counts PRSum + SurchargeAmount (CalculateReservationPrice).
 */
export const shownHotelTotals = (
  hotel: { totalPrice?: number | null; agentCommissionAmount?: number | null; agentCommissionPercent?: number | null },
  legs: readonly number[],
  rate: number,
  currency: string
): IShownHotelTotals => {
  const shown = (amount: number | null | undefined) => roundCents(Number(amount ?? 0) * rate);
  const percent = Number(hotel.agentCommissionPercent ?? 0);
  const hotelShown = shown(hotel.totalPrice);
  const legsShown = legs.map((leg) => shown(leg));
  const transfer = sumCents(legsShown);
  const total = sumCents([hotelShown, transfer]);
  const commission = sumCents([
    shown(hotel.agentCommissionAmount),
    ...legs.map((leg) => shown(percentOf(leg, percent)))
  ]);
  return {
    currency,
    hotel: hotelShown,
    legs: legsShown,
    transfer,
    total,
    percent,
    commission,
    payable: roundCents(total - commission)
  };
};

/**
 * A birth date that gives each tourist of the search party the age it was searched with: the Details window prices the transfer for the
 * party before the agent typed any name (an adult = 30 years at check-in, a child = its age). Nothing of this is registered.
 */
export const partyBirthDate = (tourist: CheckoutTouristOut, checkIn: Date): string => {
  const childAge = !tourist.isAdult && typeof tourist.age === 'number' ? tourist.age : null;
  const years = childAge ?? 30;
  const d = new Date(Date.UTC(checkIn.getUTCFullYear() - years, checkIn.getUTCMonth(), checkIn.getUTCDate()));
  if (childAge !== null) d.setUTCMonth(d.getUTCMonth() - 6);
  return d.toISOString().slice(0, 10);
};

/** The car to keep when the cars are listed again: the same id, else the same source and name (a live car's id may change). */
export const sameCar = (
  cars: CheckoutTransferPrice[],
  id: string | null | undefined,
  seen: Pick<CheckoutTransferPrice, 'name' | 'source'> | null | undefined
): CheckoutTransferPrice | null =>
  (id ? cars.find((c) => c.id === id) : undefined) ??
  (seen ? cars.find((c) => c.source === seen.source && c.name === seen.name) : undefined) ??
  null;

/** One row of the Transfer table (HOTELTRANSFER-001): a car with its own price on each way; a way that does not sell it is null. */
export interface ITransferRow {
  arrival: CheckoutTransferPrice | null;
  departure: CheckoutTransferPrice | null;
  /** the car the row is named by: the arrival's, else the departure's */
  lead: CheckoutTransferPrice;
}

/**
 * The same car on both ways, by what the agent sees: a car has another id on each way (the live price id hashes the direction, a local one is
 * the row of its own route), so ids cannot pair them. `supplierName` is not in the key: without website-details-tags the answer drops it, while
 * `source` stays (R-WF110).
 */
const carKey = (car: CheckoutTransferPrice): string =>
  [car.source, car.name, !!car.perPersonPrice, car.adultPlaces ?? 0].join('|');

/**
 * R-HTT2 / R-HTT18 / R-HTT19: each car once with its price per way. Within one key an arrival car pairs first with a still unpaired departure car
 * of the SAME price (a name with several tariffs, e.g. GROUP 372.60 / 1 362.95 / 2 555.73, must not put two tariffs in one row); what is left
 * of the key then pairs in order, Nth to Nth (one car whose price differs per way stays one row). Two cars of one way never share a row, so a
 * mismatch gives two rows - never a wrong car. Rows follow the arrival list as the server sorted it; departure-only cars come after, in their own
 * order. Independent of what is chosen, so a click never rebuilds the rows.
 */
export const transferRows = (
  arrivalCars: readonly CheckoutTransferPrice[],
  departureCars: readonly CheckoutTransferPrice[]
): ITransferRow[] => {
  const waiting = new Map<string, CheckoutTransferPrice[]>();
  for (const car of departureCars) waiting.set(carKey(car), [...(waiting.get(carKey(car)) ?? []), car]);
  const take = (arrival: CheckoutTransferPrice, accept: (departure: CheckoutTransferPrice) => boolean) => {
    const list = waiting.get(carKey(arrival)) ?? [];
    const at = list.findIndex(accept);
    return at < 0 ? null : list.splice(at, 1)[0];
  };
  const priceOf = (car: CheckoutTransferPrice) => legPrice(car, 0, 0);
  const sameFare = new Map<CheckoutTransferPrice, CheckoutTransferPrice>();
  for (const arrival of arrivalCars) {
    const departure = take(arrival, (d) => priceOf(d) === priceOf(arrival));
    if (departure) sameFare.set(arrival, departure);
  }
  const paired = new Set<CheckoutTransferPrice>();
  const rows: ITransferRow[] = arrivalCars.map((arrival) => {
    const departure = sameFare.get(arrival) ?? take(arrival, () => true);
    if (departure) paired.add(departure);
    return { arrival, departure, lead: arrival };
  });
  for (const departure of departureCars)
    if (!paired.has(departure)) rows.push({ arrival: null, departure, lead: departure });
  return rows;
};

/** R-HTT6 / R-HTT7: a click on the car a way already has takes it off; any other click picks it. `null` is the "No transfer" cell. */
export const toggledCar = (
  chosenId: string | null,
  picked: CheckoutTransferPrice | null
): CheckoutTransferPrice | null => (picked && picked.id !== chosenId ? picked : null);

/**
 * Why "Add to cart" is disabled (C3), or null: mandatory needs the airport and a car each way; mandatory / included are not sold when no
 * supplier can price the chosen airport and hotel (R-HT13); optional never blocks.
 */
export const transferBlockReason = (
  requirement: TransferRequirement | null,
  terminalCode: string | null,
  notOffered: boolean,
  arrival: CheckoutTransferPrice | null,
  departure: CheckoutTransferPrice | null
): string | null => {
  if (requirement !== 'mandatory' && requirement !== 'included') return null;
  if (!terminalCode) return 'A transfer is required for this destination. Choose the airport and a car to continue.';
  if (notOffered)
    return 'No supplier can price a transfer for this airport and hotel, so this offer cannot be booked. Choose another airport or hotel.';
  if (requirement === 'mandatory' && (!arrival || !departure))
    return 'A transfer is required for this destination. Choose the airport and a car to continue.';
  return null;
};
